import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, ScanLine, FileText, Package, Truck, Settings, LogOut, ChevronDown, Users, ShoppingBasket, ClipboardList, ShieldCheck, Scale } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, id: "nav-dashboard", owner: true },
  { to: "/scan", label: "Scan Invoice", icon: ScanLine, id: "nav-scan" },
  { to: "/order", label: "New Order", icon: ShoppingBasket, id: "nav-order" },
  { to: "/orders", label: "Orders", icon: ClipboardList, id: "nav-orders" },
  { to: "/invoices", label: "Invoices", icon: FileText, id: "nav-invoices" },
  { to: "/products", label: "Products", icon: Package, id: "nav-products", owner: true, perm: "manage_suppliers" },
  { to: "/prices", label: "Price Comparison", icon: Scale, id: "nav-prices", owner: true, perm: "view_prices" },
  { to: "/suppliers", label: "Suppliers", icon: Truck, id: "nav-suppliers", owner: true, perm: "manage_suppliers" },
  { to: "/team", label: "Team", icon: Users, id: "nav-team", owner: true, perm: "manage_team" },
  { to: "/settings", label: "Settings", icon: Settings, id: "nav-settings" },
  { to: "/admin", label: "Admin", icon: ShieldCheck, id: "nav-admin", superadmin: true },
];

const byId = (id) => NAV.find((n) => n.id === id);
const MOBILE_OWNER = ["nav-dashboard", "nav-invoices", "nav-scan", "nav-order", "nav-orders"].map(byId);
const MOBILE_STAFF = ["nav-invoices", "nav-orders", "nav-scan", "nav-order", "nav-settings"].map(byId);

export function Logo({ light = true }) {
  return (
    <div className="flex items-center gap-2 select-none" data-testid="app-logo">
      <span className="h-8 w-8 rounded-md bg-emerald-500 grid place-items-center text-slate-950 font-display font-extrabold text-lg">S</span>
      <span className={`font-display font-bold text-xl tracking-tight ${light ? "text-white" : "text-slate-900"}`}>Supplivo</span>
    </div>
  );
}

function RestaurantPicker({ dark }) {
  const { restaurants, restaurantId, selectRestaurant } = useAuth();
  if (!restaurants.length) return null;
  return (
    <div className="relative">
      <select
        data-testid="restaurant-select"
        value={restaurantId}
        onChange={(e) => selectRestaurant(e.target.value)}
        className={`appearance-none w-full rounded-md border px-3 py-2 pr-8 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-emerald-500 ${dark ? "bg-slate-800 border-slate-700 text-slate-100" : "bg-white border-slate-200 text-slate-800"}`}
      >
        {restaurants.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
      </select>
      <ChevronDown className={`pointer-events-none absolute right-2 top-2.5 h-4 w-4 ${dark ? "text-slate-400" : "text-slate-500"}`} />
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, organisation, logout, isOwner, can } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Super Admin area is platform-only: no restaurant menus. Owner without a customer account always lives here.
  const platformOnly = user?.is_superadmin && (!organisation || pathname.startsWith("/admin"));
  const nav = platformOnly
    ? [...NAV.filter((n) => n.superadmin), ...(organisation ? [{ to: isOwner ? "/dashboard" : "/scan", label: `Open ${organisation.name}`, icon: LayoutDashboard, id: "nav-open-app" }] : [])]
    : NAV.filter((n) => (isOwner || !n.owner) && (!n.perm || can(n.perm)) && (!n.superadmin || user?.is_superadmin));
  const mobile = platformOnly ? nav : isOwner ? MOBILE_OWNER : MOBILE_STAFF;

  return (
    <div className="min-h-screen bg-slate-50 lg:flex">
      <aside className="hidden lg:flex lg:flex-col w-[260px] fixed inset-y-0 left-0 bg-slate-950 text-slate-200 px-4 py-6 gap-6" data-testid="sidebar">
        <Logo />
        {!platformOnly && <div className="space-y-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 px-1">Restaurant</p>
          <RestaurantPicker dark />
        </div>}
        <nav className="flex-1 space-y-1">
          {nav.map(({ to, label, icon: Icon, id }) => (
            <NavLink key={to} to={to} data-testid={id} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
              <Icon className="h-4.5 w-4.5" size={18} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-slate-800 pt-4 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-slate-800 grid place-items-center text-sm font-semibold text-emerald-300" data-testid="user-avatar">
            {(user?.name || user?.email || "?").slice(0, 1).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate" data-testid="user-name">{user?.name}</p>
            <p className="text-xs text-slate-500 truncate">{organisation?.name}{user?.is_superadmin && (organisation ? " · Platform admin" : "Supplivo · Platform admin")}{user?.role === "staff" && " · Staff"}{user?.role === "admin" && " · Admin"}</p>
          </div>
          <button onClick={() => logout().then(() => navigate("/login"))} className="text-slate-400 hover:text-white transition-colors" title="Log out" data-testid="logout-button">
            <LogOut size={18} />
          </button>
        </div>
      </aside>

      <div className="flex-1 lg:ml-[260px] flex flex-col min-h-screen">
        <header className="lg:hidden sticky top-0 z-30 bg-slate-950 text-white px-4 py-3 flex items-center justify-between gap-3" data-testid="mobile-header">
          <Logo />
          <div className="w-44"><RestaurantPicker dark /></div>
        </header>
        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-6 lg:py-8 pb-24 lg:pb-8 max-w-[1400px] w-full">
          {user?.must_change_password && (
            <Link to="/settings" className="block mb-4 text-sm bg-amber-50 border border-amber-300 text-amber-900 rounded-md px-4 py-2.5" data-testid="temp-password-banner">You're signed in with a temporary password — tap to set your own.</Link>
          )}
          {children}
        </main>
        <nav className={`lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white border-t border-slate-200 grid px-1 pb-[env(safe-area-inset-bottom)] grid-cols-5`} data-testid="bottom-nav">
          {mobile.map(({ to, label, icon: Icon, id }) => {
            const isScan = to === "/scan";
            return (
              <NavLink key={to} to={to} data-testid={`mobile-${id}`} className={({ isActive }) => `flex flex-col items-center justify-center gap-1 py-2 text-[11px] font-medium ${isActive ? "text-emerald-600" : "text-slate-500"}`}>
                {isScan ? (
                  <span className="-mt-6 h-14 w-14 rounded-full bg-emerald-500 text-white grid place-items-center shadow-lg shadow-emerald-500/30"><Icon size={26} /></span>
                ) : <Icon size={22} />}
                <span>{isScan ? "Scan" : label === "New Order" ? "Order" : label}</span>
              </NavLink>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
